import { DownloadIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';
import { CSVLink } from "react-csv";
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/use-toast';

function isValidEmail(value) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value.trim());
}

export default function ExportUserGDPRDataDialog({ dropdown }) {
    const [isOpen, setIsOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [userData, setUserData] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [isFetchError, setIsFetchError] = useState(false);
    const [notFoundText, setNotFoundText] = useState("");
    const [isDownloadingEnabled, setIsDownloadingEnabled] = useState(false);
    const [typingTimer, setTypingTimer] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchUserData = async () => {
        if (!email) {
            setUserData(null);
            setCsvData([]);
            setIsFetchError(false);
            setNotFoundText("");
            setIsDownloadingEnabled(false);
            return;
        }

        if (!isValidEmail(email)) {
            return;
        }

        setIsLoading(true); // Start loader

        try {
            const response = await api.get(
                `https://skhrf-api.cmdigital.se/v1/users/gdpr?email=${email}`
            );

            setIsFetchError(false);
            setNotFoundText("");

            const user = response.data || null;
            setUserData(user);

            const transformedData = user ? [user] : [];
            setCsvData(transformedData);

            setIsDownloadingEnabled(Boolean(user));
        } catch (error) {
            if (error?.response?.status === 404) {
                setIsFetchError(true);
                setNotFoundText("Ingen användare hittades med den angivna e-postadressen.");
                setIsDownloadingEnabled(false);
                setUserData(null);
                setCsvData([]);
            }
        } finally {
            setIsLoading(false); // Stop loader
        }
    };

    // Debounce logic: wait 1.5s after typing stops
    useEffect(() => {
        // As soon as the user types anything new:
        // - disable download button
        // - clear any 404 error state
        setIsDownloadingEnabled(false);
        setIsFetchError(false);
        setNotFoundText("");

        if (typingTimer) {
            clearTimeout(typingTimer);
        }

        if (email.trim().length > 0) {
            const timer = setTimeout(() => {
                fetchUserData();
            }, 1500);
            setTypingTimer(timer);
        } else {
            setUserData(null);
            setCsvData([]);
        }

        return () => {
            if (typingTimer) {
                clearTimeout(typingTimer);
            }
        };
    }, [email]);

    const headers = [
        { label: "ID", key: "id" },
        { label: "First Name", key: "first_name" },
        { label: "Last Name", key: "last_name" },
        { label: "Email", key: "email" },
        { label: "Phone", key: "phone" },
        { label: "Address", key: "address" },
        { label: "Parent Email", key: "parent_email" },
        { label: "Birth Year", key: "birth_year" },
        { label: "Created At", key: "created_at" },
        { label: "Superadmin", key: "superadmin" },
        { label: "Judge", key: "judge" },
        { label: "Bunnyadmin", key: "bunnyadmin" },
        { label: "Club ID", key: "club_id" },
        { label: "Role", key: "role" },
        { label: "Membership Started", key: "membership_started" },
        { label: "Membership Expiration", key: "membership_expiration" },
        { label: "Minimember", key: "minimember" },
        { label: "Familymembership", key: "familymembership" },
        { label: "Suspended At", key: "suspended_at" },
        { label: "Suspended Reason", key: "suspended_reason" },
        { label: "Last Password Change", key: "last_password_change" },
        { label: "Legacy Membership", key: "legacy_membership" },
        { label: "Redacted At", key: "redacted_at" },
    ];

    return (
        <Dialog
            onOpenChange={(open) => {
                setIsOpen(open);
                if (open) {
                    setEmail("");
                    setIsFetchError(false);
                    setNotFoundText("");
                    setCsvData([]);
                    setIsDownloadingEnabled(false);
                }
            }}
            open={isOpen}
        >
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className='flex items-center w-full'>
                        <DownloadIcon className='mr-3'/>
                        Exportera användardata
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                <DialogTrigger className='flex items-center w-full' asChild>
                    <Button>
                        <DownloadIcon className='mr-3'/>
                        Exportera användardata
                    </Button>
                </DialogTrigger>
            )}

            <DialogContent
                className="flex flex-col gap-6"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <DialogHeader className="text-left">
                    <DialogTitle>Exportera användardata</DialogTitle>
                    <DialogDescription>
                        Ange e-postadress till användare för att ladda ner användardata. Det kan ta några sekunder att hämta data och göra nedladdningen tillgänglig.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-2">
                    <Label htmlFor='email'>Användarens e-postadress</Label>
                    <Input
                        id="email"
                        type="email"
                        placeholder="hej@cmdigital.se"
                        autoComplete='off'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={isFetchError ? 'border-red-500' : ''}
                    />
                    {notFoundText && (
                        <p className="text-red-500 text-sm">{notFoundText}</p>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <DialogClose asChild>
                        <Button variant="secondary">Stäng</Button>
                    </DialogClose>
                    <Button disabled={!isDownloadingEnabled}>
                        {isLoading ? (
                            <UpdateIcon className='loader-spinner' />
                        ) : (
                            isDownloadingEnabled ? (
                                <CSVLink
                                    data={csvData}
                                    headers={headers}
                                    filename={`user_data_${email}.csv`}
                                    className="flex items-center justify-center w-full"
                                >
                                    <DownloadIcon className="mr-3" />
                                    Ladda ner
                                </CSVLink>
                            ) : ('Ange e-post')
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
